import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useFocusTrap from '../../hooks/useFocusTrap';
import MarketplaceCartSummary from './MarketplaceCartSummary';
import { navigateToMarketplaceCartSignup } from './checkoutFlow';
import { getUserAccountHref } from '../Header/accountIdentity';
import './MarketplaceCart.scss';

const CartIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M3.75 5.25H6.25L8.45 14.1C8.6 14.7 9.14 15.12 9.76 15.12H18.18C18.76 15.12 19.28 14.75 19.47 14.2L21 9.48H7.18"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
        />
        <circle cx="10.25" cy="19" r="1.45" fill="currentColor" />
        <circle cx="17.5" cy="19" r="1.45" fill="currentColor" />
    </svg>
);

const ChevronIcon = ({ direction }) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d={direction === 'right' ? 'M9 5.5L15.5 12 9 18.5' : 'M15 5.5L8.5 12 15 18.5'}
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
        />
    </svg>
);

const SyncIndicator = ({ visible }) => {
    if (!visible) {
        return null;
    }

    return (
        <span aria-hidden="true" className="marketplace-cart-status">
            <span className="marketplace-cart-status__spinner" />
        </span>
    );
};

const getMergeLineTypeLabel = (line) => {
    if (line?.display?.typeLabel) {
        return line.display.typeLabel;
    }

    return line?.type === 'bundle' ? 'Bundle' : 'Asset';
};

const MergeCartColumn = ({ cart, title }) => (
    <section className="marketplace-cart-merge__column" aria-label={title}>
        <div className="marketplace-cart-merge__column-header">
            <div>
                <span>{title}</span>
                <strong>{cart?.itemCount || 0} items</strong>
            </div>
            <div className="marketplace-cart-merge__subtotal">
                {cart?.subtotal?.formatted || 'Pending'}
            </div>
        </div>
        <div className="marketplace-cart-merge__list" role="list">
            {(cart?.lines || []).map((line) => (
                <div className="marketplace-cart-merge__line" key={line.lineKey} role="listitem">
                    <div className="marketplace-cart-merge__line-copy">
                        <strong>{line.display?.name || 'Untitled item'}</strong>
                        <span>{getMergeLineTypeLabel(line)}</span>
                    </div>
                    <div className="marketplace-cart-merge__line-price">
                        {line.display?.priceLabel || 'Pending'}
                    </div>
                </div>
            ))}
            {(!cart?.lines || !cart.lines.length) &&
            <div className="marketplace-cart-merge__empty">
                No items
            </div>
            }
        </div>
    </section>
);

export const MarketplaceCartTrigger = ({ snapshot, onOpen, className = '', mobile = false }) => {
    const badgeText = snapshot.itemCount > 99 ? '99+' : `${snapshot.itemCount}`;

    return (
        <button
            aria-label="Open cart"
            className={`marketplace-cart-trigger ${mobile ? 'is-mobile' : 'is-desktop'} ${className}`.trim()}
            onClick={onOpen}
            type="button"
        >
            <span className="marketplace-cart-trigger__icon">
                <CartIcon />
            </span>
            {snapshot.itemCount > 0 &&
            <span className="marketplace-cart-trigger__badge" aria-hidden="true">
                {badgeText}
            </span>
            }
            <span className="screen-reader-only">
                {snapshot.itemCount > 0 ? `${snapshot.itemCount} items in cart` : 'Cart is empty'}
            </span>
        </button>
    );
};

export const MarketplaceCartLayer = ({ snapshot, controller, topOffset = 0 }) => {
    const drawerRef = useRef(null);
    const mergeModalRef = useRef(null);
    const [liveMessage, setLiveMessage] = useState('');
    const [checkoutPending, setCheckoutPending] = useState(false);
    const [mergePending, setMergePending] = useState('');
    const isGuest = !controller.session?.user && !controller.session?.isAuthenticating;
    const subscribeHref = getUserAccountHref({
        pathname: isGuest ? '/register/cart-subscribe' : '/upgrade?campaign=cart-subscribe',
    });
    const subscribeLabel = 'Unlimited access';
    const badgeText = snapshot.itemCount > 99 ? '99+' : `${snapshot.itemCount}`;
    const drawerStyle = { '--marketplace-cart-top-offset': `${Math.max(0, Number(topOffset) || 0)}px` };
    const mergeComparison = snapshot.mergeComparison;

    useFocusTrap(drawerRef, snapshot.isOpen);
    useFocusTrap(mergeModalRef, snapshot.mergeRequired);

    useEffect(() => {
        if (!snapshot.isOpen) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                controller.close();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [controller, snapshot.isOpen]);

    useEffect(() => {
        const handleCartChanged = (event) => {
            const reason = event?.detail?.reason;
            if (reason === 'item-added') {
                setLiveMessage('Item added to cart');
                return;
            }

            if (reason === 'item-removed') {
                setLiveMessage('Item removed from cart');
            }
        };

        window.addEventListener('crate:cart:changed', handleCartChanged);

        return () => {
            window.removeEventListener('crate:cart:changed', handleCartChanged);
        };
    }, []);

    useEffect(() => {
        if (!liveMessage) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setLiveMessage('');
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [liveMessage]);

    const handleCheckout = async () => {
        if (isGuest) {
            navigateToMarketplaceCartSignup();
            return;
        }

        setCheckoutPending(true);
        try {
            await controller.checkout();
        } finally {
            setCheckoutPending(false);
        }
    };

    const handleResolveMerge = async (action) => {
        setMergePending(action);
        try {
            await controller.resolveMerge(action);
        } finally {
            setMergePending('');
        }
    };

    const handleCartTabClick = () => {
        if (snapshot.isOpen) {
            controller.close();
            return;
        }

        controller.open();
    };

    const layer = (
        <>
            <div className="marketplace-cart-live-region" aria-live="polite">
                {liveMessage}
            </div>
            <div className="marketplace-cart-layer" ref={drawerRef} style={drawerStyle}>
                {(snapshot.itemCount > 0 || snapshot.isOpen) &&
                <button
                    aria-label={snapshot.isOpen ? 'Collapse cart' : 'Open cart'}
                    className={`marketplace-cart-drawer-tab ${snapshot.isOpen ? 'is-open' : ''}`}
                    onClick={handleCartTabClick}
                    type="button"
                >
                    <span className="marketplace-cart-drawer-tab__chevron">
                        <ChevronIcon direction={snapshot.isOpen ? 'right' : 'left'} />
                    </span>
                    {!snapshot.isOpen &&
                    <span className="marketplace-cart-drawer-tab__icon">
                        <CartIcon />
                    </span>
                    }
                    {!snapshot.isOpen && snapshot.itemCount > 0 &&
                    <span className="marketplace-cart-drawer-tab__badge" aria-hidden="true">
                        {badgeText}
                    </span>
                    }
                </button>
                }
                <aside
                    aria-hidden={!snapshot.isOpen}
                    aria-label="Shopping cart"
                    className={`marketplace-cart-drawer ${snapshot.isOpen ? 'is-open' : ''}`}
                    role="dialog"
                >
                    <div className="marketplace-cart-drawer__header">
                        <div className="marketplace-cart-drawer__copy">
                            <h2>Shopping Cart</h2>
                            <p>Buy lifetime access to individual assets and Plugins.</p>
                        </div>
                        <div className="marketplace-cart-drawer__actions">
                            <SyncIndicator visible={checkoutPending || snapshot.syncState === 'syncing'} />
                        </div>
                    </div>
                    <div className="marketplace-cart-drawer__body">
                        <MarketplaceCartSummary
                            assetInteractionMode="event-first"
                            assetOpenEventName="crate:asset:open"
                            checkoutPending={checkoutPending}
                            embedded
                            loginHref="/login?return_to=/marketplace/cart&cart_intent=checkout"
                            onCheckout={handleCheckout}
                            onRemove={(lineKey) => controller.removeItem(lineKey)}
                            signupHref="/register"
                            subscribeHref={subscribeHref}
                            subscribeLabel={subscribeLabel}
                            snapshot={snapshot}
                        />
                    </div>
                </aside>
            </div>
            {snapshot.mergeRequired &&
            <div className="marketplace-cart-merge">
                <div className="marketplace-cart-merge__overlay" />
                <div className="marketplace-cart-merge__dialog" ref={mergeModalRef} role="dialog">
                    <h2>You already have items in your cart</h2>
                    <p>
                        Choose how to reconcile your local cart and account cart before checkout.
                    </p>
                    <div className="marketplace-cart-merge__comparison">
                        <MergeCartColumn cart={mergeComparison?.localCart} title="This cart" />
                        <MergeCartColumn cart={mergeComparison?.accountCart} title="Account cart" />
                    </div>
                    <div className="marketplace-cart-merge__actions">
                        <button
                            autoFocus
                            className="marketplace-cart-button is-primary"
                            disabled={!!mergePending}
                            onClick={() => handleResolveMerge('keep-both-carts')}
                            type="button"
                        >
                            {mergePending === 'keep-both-carts' ? 'Working...' : 'Merge carts'}
                        </button>
                        <button
                            className="marketplace-cart-button is-secondary"
                            disabled={!!mergePending}
                            onClick={() => handleResolveMerge('keep-account-cart')}
                            type="button"
                        >
                            Use account cart
                        </button>
                        <button
                            className="marketplace-cart-inline-button is-standalone"
                            disabled={!!mergePending}
                            onClick={() => handleResolveMerge('keep-local-cart')}
                            type="button"
                        >
                            Use this cart
                        </button>
                    </div>
                </div>
            </div>
            }
        </>
    );

    if (typeof document === 'undefined' || !document.body) {
        return layer;
    }

    return createPortal(layer, document.body);
};
